import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * EnvironmentService
 *
 * Exposes runtime environment signals:
 * - online / offline
 * - tab visibility
 *
 * No sync logic. No timers. No side effects.
 */
@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  /* ------------------------------------------------------------------
   * INTERNAL SUBJECTS
   * ------------------------------------------------------------------ */

  private readonly onlineSubject = new BehaviorSubject<boolean>(navigator.onLine);

  private readonly visibilitySubject =
    new BehaviorSubject<'visible' | 'hidden'>(
      document.visibilityState === 'visible' ? 'visible' : 'hidden'
    );

  /* ------------------------------------------------------------------
   * PUBLIC OBSERVABLES (READ-ONLY)
   * ------------------------------------------------------------------ */

  /** Emits true when browser is online */
  readonly online$: Observable<boolean> = this.onlineSubject.asObservable();

  /** Emits 'visible' or 'hidden' */
  readonly visibility$: Observable<'visible' | 'hidden'> =
    this.visibilitySubject.asObservable();

  constructor(private readonly zone: NgZone) {
    this.bindBrowserEvents();
  }

  /** Synchronous online check, for call sites that can't subscribe to online$. */
  isOnline(): boolean {
    return this.onlineSubject.value;
  }

  /* ------------------------------------------------------------------
   * EVENT BINDINGS
   * ------------------------------------------------------------------ */

  private bindBrowserEvents(): void {
    // Online / offline
    window.addEventListener('online', () =>
      this.zone.run(() => this.onlineSubject.next(true))
    );

    window.addEventListener('offline', () =>
      this.zone.run(() => this.onlineSubject.next(false))
    );

    // Visibility (important for iOS background throttling)
    document.addEventListener('visibilitychange', () =>
      this.zone.run(() =>
        this.visibilitySubject.next(
          document.visibilityState === 'visible' ? 'visible' : 'hidden'
        )
      )
    );
  }
}